import {
  Breadcrumb,
  Button,
  message,
  notification,
  TableProps,
} from "antd";
import TemplateStyles from "./TemplateList.module.css";
import {
  getColumnSearchProps,
  getColumnSelectProps,
} from "../../../../shared/base-components/Filter/Filters";
import { useEffect, useState } from "react";
import {
  Template,
  TemplateFilter,
  TemplateFilterParams,
} from "./TemplateList.types";
import TemplateService from "../../services/TemplateService";
import {
  CREATE_TEMPLATE_BUTTON_LABEL,
  DEFAULT_GUID,
  ENGINE_NAME_LABEL,
  FILTER_CREATED_BY_LABEL,
  FILTER_CREATED_ON_LABEL,
  IMPORT_TEMPLATE_BUTTON_LABEL,
  NAME_KEY,
  NO_OF_DELIVERABLES_LABEL,
  TOAST_DEFAULT_ERROR_MESSAGE,
} from "../../../../shared/utils/CommonConstants";
import { CreateModal } from "../../../../shared/screen-components/CommonCreateComponent/CommanCreateComponent";
import { useNavigate } from "react-router-dom";
import { Container } from "../../../../shared/base-components/Layouts/Container/Container.styles";
import { Flex } from "../../../../shared/base-components/Layouts/Flex/Flex.styles";
import { PagedResponse } from "../../../../shared/types/PagedResponse";
import { SearchInput } from "../../../../shared/base-components/Search/Search";
import { Table } from "../../../../shared/base-components/Table/Table";

export const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [filterOptions, setFilterOptions] = useState<TemplateFilter>({
    createdUserList: [],
  });

  const [templateNameFilter, setTemplateNameFilter] = useState<string | null>(
    null
  );
  const [createdByFilter, setCreatedByFilter] = useState<string | null>(null);
  const [createdOnFilter, setCreatedOnFilter] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState<string>("");

  const columns: TableProps["columns"] = [
    {
      title: ENGINE_NAME_LABEL,
      dataIndex: "templateName",
      ...getColumnSearchProps(
        "templateName",
        ENGINE_NAME_LABEL,
        templateNameFilter,
        setTemplateNameFilter
      ),
      width: "25%",
    },
    {
      title: FILTER_CREATED_BY_LABEL,
      dataIndex: "createdByUserName",
      ...getColumnSelectProps(
        "createdBy",
        FILTER_CREATED_BY_LABEL,
        filterOptions.createdUserList.map((t) => {
          return { value: t.id, label: t.userName };
        }),
        createdByFilter,
        setCreatedByFilter,
        !!createdByFilter && createdByFilter !== DEFAULT_GUID
      ),
      width: "25%",
    },
    {
      title: FILTER_CREATED_ON_LABEL,
      dataIndex: "createdOn",
      ...getColumnSearchProps(
        "createdOn",
        FILTER_CREATED_ON_LABEL,
        createdOnFilter,
        setCreatedOnFilter,
        true
      ),
      width: "25%",
    },
    {
      title: NO_OF_DELIVERABLES_LABEL,
      key: "deliverableCount",
      dataIndex: "deliverableCount",
      width: "25%",
    },
  ];

  useEffect(() => {
    fetchFilterOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData(1, 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, templateNameFilter, createdByFilter, createdOnFilter]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const templateService = new TemplateService();

  const fetchData = async (
    currentPage: number,
    pageSize: number
  ): Promise<void> => {
    setIsLoading(true);
    try {
      // Construct filterObject
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const selectedCreatedBy = filterOptions.createdUserList.find(
        (createdBy) => createdBy.userName === createdByFilter
      );

      const date = createdOnFilter ? new Date(createdOnFilter) : null;
      const formattedDate = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(date.getDate()).padStart(2, "0")}`
        : null;

      const filterObject: TemplateFilterParams = {
        templateName: templateNameFilter || "",
        createdBy: createdByFilter ?? DEFAULT_GUID,
        createdOn: formattedDate || null,
        startIndex: currentPage - 1,
        itemsPerPage: pageSize,
        searchValue: searchValue ?? null,
      };

      const response = await templateService.getAllTemplates<
        PagedResponse<Template[]>
      >(filterObject);

      setTemplates(response.items);
      setTotal(response.totalCount);
    } catch (error) {
      notification.error({ message: TOAST_DEFAULT_ERROR_MESSAGE });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterOptions = async (): Promise<void> => {
    try {
      const response: TemplateFilter = await templateService.templatesFilter();
      setFilterOptions(response);
    } catch (error) {
      notification.error({ message: TOAST_DEFAULT_ERROR_MESSAGE });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clearFilter = () => {
    if (!!templateNameFilter || !!createdByFilter || !!createdOnFilter) {
      setTemplateNameFilter(null);
      setCreatedOnFilter(null);
      setCreatedByFilter(null);
    }
  };

  const openTemplate = async (id: number, name: string) => {
    try {
      await templateService.getRuleEngineTemplateUsingAxios(id);
      navigate(`/template/${id}/sheets/-1?${NAME_KEY}=${name}`);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };

  return (
    <Container>
      <Breadcrumb className={TemplateStyles.breadCrump}
      />
      <div className="extra">
        <Flex gap="12px">
          <SearchInput
            onSearch={(value) => setSearchValue(value)}
            placeholder="Search Templates"
          />
          {/* <Button id="template-clear-filter" onClick={clearFilter}>{CLEAR_FILTER_TEXT}</Button> */}
        </Flex>
        <Flex gap="12px">
          <Button id="template-import-button" type="primary" onClick={showModal}>
            {IMPORT_TEMPLATE_BUTTON_LABEL}
          </Button>
          <Button id="template-create-button" type="primary" onClick={() => navigate("/template/create")}>
            {CREATE_TEMPLATE_BUTTON_LABEL}
          </Button>
        </Flex>
      </div>
      <Table
        columns={columns}
        dataSource={templates}
        isLoading={isLoading}
        fetchData={fetchData}
        total={total}
        onRow={(record) => {
          return {
            onClick: () =>
              openTemplate(record.ruleEngineId, record.templateName),
          };
        }}
      />
      <CreateModal
        isVisible={isModalOpen}
        isTemplate={true}
        fetchData={fetchData}
        onCancel={() => setIsModalOpen(false)}
      />
    </Container>
  );
};
